import {
  ACTION_ARGS,
  COMPONENT_STATE_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
} from '../../../utils/spds/metadata';
import { BOOLEAN, STRING } from '../../../utils/component-validations';
import { hash } from '@ember/helper';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import ContentContainer from './accordion/content-container.gjs';
import FlexColumn from '../layout/flex/column.gjs';
import Super from '../core/base.gjs';
import Trigger from './accordion/trigger.gjs';
import styles from './accordion.module.scss';

const METADATA = {
  component: 'Spds::Container::Accordion',
  description: 'Interactive heading with a collapsible content area',
  meta: {
    ...pickArgs(ACTION_ARGS, ['onClose', 'onOpen']).meta,
    ...pickArgs(COMPONENT_STATE_ARGS, ['disabled', 'open']).meta,
    animated: {
      ...COMPONENT_STATE_ARGS.meta.animated,
      defaultValue: true,
    },
    contentColumn: {
      type: BOOLEAN,
      description: 'Display content in column layout',
      defaultValue: true,
    },
    contentGrid: {
      type: BOOLEAN,
      description: 'Display content in grid layout',
      defaultValue: false,
    },
    icon: {
      type: STRING,
      description: 'Icon to display in trigger',
      defaultValue: 'dropdown-caret',
    },
    triggerGap: {
      type: STRING,
      description: 'Gap between icon and text in trigger',
      defaultValue: '12px',
    },
    triggerJustifyContent: {
      type: STRING,
      description: 'Justify content alignment in trigger',
      defaultValue: 'flex-start',
    },
  },
  name: 'Accordion',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=18965-95563&m=dev',
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/container/accordion.gjs',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/59ce8f-accordion',
  },
};

export default class SpdsContainerAccordion extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-container-accordion';
  classesToMapToAttrs = ['open'];
  styles = styles;

  @tracked _open;

  constructor() {
    super(...arguments);

    this.arguments = this.composeArguments(METADATA);
  }

  get animated() {
    return this.args.animated ?? getDefaultValueFromMetadata(METADATA, 'animated');
  }

  get contentContainerId() {
    return this.composeSubcomponentId('content-container');
  }

  get open() {
    return this._open ?? getDefaultValueFromMetadata(METADATA, 'open');
  }

  set open(value) {
    this._open = value;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  toggleOpenFromExternalSource = modifier(() => {
    let { open } = this.args;

    if (isEmpty(open)) return;

    this.open = open;
  });

  toggleOpen = () => {
    this.open = !this.open;

    this.open ? this.args.onOpen?.() : this.args.onClose?.();
  };

  <template>
    <FlexColumn
      class={{this.classes}}
      data-test-spds-container-accordion
      {{this.setupComponent}}
      {{this.toggleOpenFromExternalSource}}
      {{this.validateArguments}}
      ...attributes
    >
      {{yield
        (hash
          Trigger=(component
            Trigger
            contentContainerId=this.contentContainerId
            disabled=@disabled
            gap=@triggerGap
            icon=@icon
            id=this.triggerId
            justifyContent=@triggerJustifyContent
            onClick=this.toggleOpen
            open=this.open
          )
          ContentContainer=(component
            ContentContainer
            animated=this.animated
            column=@contentColumn
            grid=@contentGrid
            id=this.contentContainerId
            open=this.open
            reverse=@reverse
            triggerId=this.triggerId
          )
        )
      }}
    </FlexColumn>
  </template>
}

export const ACCORDION_METADATA = SpdsContainerAccordion.metadata;
